import { useState, useEffect, useRef } from 'react';
import { Card, Chip } from '@safetyhub/ui';
import { Link } from 'react-router-dom';
import {
  releaseNotesApi,
  PublicReleaseNoteDateGroup,
  ReleaseNotePlatform,
  ReleaseNoteCategory,
} from '../../lib/api';
import { useAuthStore } from '../../stores/auth';

/**
 * surface 값의 화면 문구 — `ReleaseNotePlatform` 일곱 값 전부에 대해 exhaustive하다.
 *
 * 칩이 쓰는 것은 {@link SINGLE_PLATFORM_CHIPS} 셋뿐이지만, **그룹 라벨**(`platforms.map(…)`)은
 * 서버가 돌려주는 값을 그대로 그리므로 **유산 값도 라벨이 있어야 한다** — 없으면 `undefined`가
 * 그려져 빈 칸이 된다. `INTERNAL`은 공개 피드에서 서버가 배제하므로 이 화면에 **도달하지 않지만**,
 * 「도달하지 않는다」를 타입으로 좁히면(위 유니온 축소) admin 조회 쪽이 거짓이 되므로 라벨을 둔다.
 */
const PLATFORM_LABELS: Record<ReleaseNotePlatform, string> = {
  ALL: '전체',
  WEB: '웹',
  MOBILE: '모바일',
  APP: '앱',
  ADMIN: '관리자',
  SERVER: '서버',
  INTERNAL: '내부',
};

const CATEGORY_LABELS: Record<ReleaseNoteCategory, string> = {
  FEATURE: '새 기능',
  IMPROVEMENT: '개선',
  BUGFIX: '버그 수정',
  SECURITY: '보안',
};

const CATEGORY_COLORS: Record<ReleaseNoteCategory, string> = {
  FEATURE: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  IMPROVEMENT: 'text-sky-700 bg-sky-50 border-sky-200',
  BUGFIX: 'text-rose-700 bg-rose-50 border-rose-200',
  SECURITY: 'text-amber-700 bg-amber-50 border-amber-200',
};

// 값 구분 팔레트 — 목업 세만틱 5색(blue/emerald/amber/rose/violet) + 중립 ink로 수렴.
// SERVER는 orange가 5색 밖이라 amber로 접는다(구분은 라벨 텍스트가 한다).
// APP은 MOBILE과 **같은 emerald**다 — 같은 surface의 선행/후계 값이라 색을 가르면 둘이 다른 것으로
// 읽힌다(과도기에 두 값이 한 화면에 섞일 수 있다). 구분은 라벨 텍스트가 한다.
const PLATFORM_COLORS: Record<ReleaseNotePlatform, string> = {
  ALL: 'text-ink-600',
  WEB: 'text-blue-600',
  MOBILE: 'text-emerald-600',
  APP: 'text-emerald-600',
  ADMIN: 'text-violet-600',
  SERVER: 'text-amber-600',
  INTERNAL: 'text-ink-500',
};

/**
 * 위 두 표에 **없는** surface 값이 왔을 때의 표시 — 고객에게 뜻이 통하는 일반 문구로 떨어진다.
 *
 * ## 이 폴백이 덮는 창 — **사본 래칫의 대체가 아니다**
 * 사본(`lib/api.ts`의 `ReleaseNotePlatform`)이 낡는 것 자체는
 * `scripts/release-notes/frontend-surface-copies.test.mjs`가 **양방향 래칫**으로 막는다. 그 검사가
 * 초록이어도 **배포 창**이 남는다: `deploy-web`(Cloudflare 26s)과 `deploy-ec2`(마이그레이션 65s)가
 * `all-tests-passed` 뒤 **병렬**이라 서버 enum이 먼저 넓어질 수 있고, 그보다 긴 창은 **캐시된 옛
 * 번들**이다(고객 탭이 새로고침할 때까지 옛 라벨 표를 들고 있다 — 이 레포는 그 창을 한 번 치렀다).
 * ⇒ 둘은 **다른 것을 막는다.** 이 폴백을 근거로 그 래칫을 끄지 말 것이고, 그 반대도 아니다.
 *
 * ## 왜 raw 값을 쓰지 않나 — admin과 **일부러 다르다**
 * 여기는 고객 화면이다. `SERVER`·`INTERNAL` 같은 내부 어휘를 그대로 보이면 뜻이 통하지 않고 고객이
 * 할 수 있는 일도 없다(이 레포는 raw enum 누출로 한 번 데였다 —
 * `memory/feedback-display-fallback-leaks-raw-enum`). **admin은 반대로 raw를 그대로 보여준다** —
 * 운영자가 그것을 보고 「사본이 낡았다」를 알아차리는 것이 목적이기 때문이다(사용자 결정 2026-10-15).
 *
 * ⚠️ 이것은 `PLATFORM_LABELS`를 **좁히는 것이 아니다** — 일곱 값은 그대로 유지한다(유산 값도 옛 행이
 * 들고 있다). 폴백은 그 Record **밖**의 값을 받는 자리다.
 * ⚠️ 타입은 이 `??`를 **불필요해 보이게** 만든다(`Record<ReleaseNotePlatform, string>` 조회는 늘
 * `string`으로 타입된다). 그것이 바로 이 폴백이 필요한 이유다 — 낡은 창에서는 **타입이 거짓**이고
 * 런타임 값이 그 유니온 밖이다. 「죽은 코드」로 읽고 지우지 말 것.
 *
 * 짝 spec(`__tests__/index.defaultScope.test.tsx`)의 「모르는 surface」 케이스가 **이 축의 유일한
 * 탐지기**다.
 */
const UNKNOWN_PLATFORM_LABEL = '기타';
const UNKNOWN_PLATFORM_COLOR = 'text-ink-600';

/** 그룹 라벨·색은 **서버가 돌려준 값**을 그리므로 이 두 함수를 거친다(칩은 아니다 — 아래 주석). */
const platformLabel = (platform: ReleaseNotePlatform): string =>
  PLATFORM_LABELS[platform] ?? UNKNOWN_PLATFORM_LABEL;
const platformColor = (platform: ReleaseNotePlatform): string =>
  PLATFORM_COLORS[platform] ?? UNKNOWN_PLATFORM_COLOR;

/**
 * 이 화면의 **기본 스코프** — `WEB` ∪ `APP`, 그리고 **유산 두 값**(`MOBILE`·`ALL`)
 * (2026-10-15, 결정 `2026-10-15-release-note-surfaces-as-a-set-and-popup-decisions.md` §A ⑦).
 *
 * `apps/web`이 PWA다(`vite.config.ts`의 `VitePWA`) — 모바일 사용자가 **이 화면의 사용자**이고 `APP` 노트는
 * 전부 web 코드의 모바일 기능(두 손가락 확대·PWA 자동 갱신·환경 카메라)이다. **`APP`을 빼면 그 사용자가
 * 자기 노트를 못 본다.** 되짚을 조건: **네이티브 앱 전환** — 그때 web은 `WEB`만, 앱은 `APP`만 보도록 가른다.
 *
 * ADMIN은 기본에서 빠진다 — 고객이 접근할 수 없는 양식 저작·운영자 매뉴얼이다. ~~칩으로는 **여전히 고를 수
 * 있다**(궁금한 사람이 넓혀 볼 수 있게 둔다).~~ **2026-10-15 개정: 칩에서도 빼기로 결정했다**(사용자
 * 결정 — 「업데이트 내역은 **본인에게 해당되는 것**만 나오는 것이 맞다」). 「전체를 볼 수단」은 **플랫폼
 * 축이 아니라 「전체 이력」 축**이었다 — 사용자 원문의 목적어가 「**페이지**」였고(「전체 업데이트 내역이나
 * 전체 공지를 확인할 수 있는 **페이지**도 있어야 하는데」) 그것을 칩으로 대신한 것이 범주 오류였다.
 * ADMIN 노트를 보는 자리는 **admin 화면**이다(그쪽 목록은 필터가 없으면 전 플랫폼을 돌려준다 —
 * `ReleaseNotesService.findAll`의 `where`가 `platform`이 없으면 조건 0개다).
 * ⚠️ **보안 보정이 아니다** — 공개 API는 그 값을 **여전히 돌려주고**(`platforms=ADMIN`을 손으로 보내면
 * 온다) 서버가 배제하는 것은 `INTERNAL`뿐이다. 이것은 **표시 정리**다. 그래서 이 상수는 그대로다.
 * `INTERNAL`은 **보내지 않는다** — 서버가 공개 피드에서 집행하지만(결정
 * `2026-10-15-internal-is-an-explicit-empty-surface-and-the-server-owns-its-enforcement.md`) 클라이언트가
 * 그것을 **청하지 않는 것**은 별 축이고 짝 spec이 그 축을 잰다.
 *
 * ## 유산 두 값을 «남기는» 이유 — 둘 다 「두 경우 모두 옳아서」다
 * `MOBILE`은 `APP`의 선행 값이고 `ALL`은 걷어낸 「전부」 태그라(§A ③④) 재분류가 반영된 뒤엔 **데이터에
 * 0건**이다. 그런데도 보내는 이유는 **배포가 두 job으로 갈리기 때문**이다 — web은 Cloudflare,
 * 마이그레이션은 `deploy-ec2`라 **web이 먼저 뜨고 DB가 아직 옛 값인 창**이 있다. 그 창에서
 * 두 값을 안 보내면 옛 행이 통째로 사라진다. 실측(2026-10-15): 두 job은 `all-tests-passed` 뒤
 * **병렬**이고 `deploy-web` **26s** · `deploy-ec2` **65s**라 web이 먼저 뜬다.
 * - `MOBILE`: 그 창의 서버엔 `resolvePublicPlatformScope`의 `MOBILE`→`APP` alias가 **아직 없으므로**
 *   MOBILE을 보내면 MOBILE 행이 그대로 맞는다. 반대로 alias가 배포된 뒤에는 `APP`으로 접혀 중복
 *   제거되니 **무해**하다. ⚠️ 그 alias는 **옛 캐시 번들이 보내는 MOBILE**을 받기 위한 것이고
 *   「새 번들이 옛 행을 보는 것」은 지켜주지 않는다 — 그것을 지키는 것이 이 줄이다.
 * - `ALL`: 집합 축에서 `ALL`은 다른 값과 같은 **원소**이고 「(공개) 전부」를 뜻하지 않는다
 *   (서버 `resolvePublicPlatformScope` JSDoc) — 그래서 보내도 ADMIN이 섞여 나오지 않는다.
 *
 * ### 되짚을 조건 (그때 두 값을 뺀다)
 * - **조건**: 재분류 배포가 안정되고 prod 데이터에서 `MOBILE`·`ALL`이 **0건**임이 확인된 뒤.
 * - **재료**: prod `release_notes` 테이블의 `platforms` 배열(배포마다 사라지는 로그가 아니라 **행**이다).
 * - **쿼리 한 줄**: `SELECT p, count(*) FROM release_notes, unnest(platforms) AS p GROUP BY 1 ORDER BY 2 DESC`
 *   — `MOBILE`·`ALL`이 **출력에 없으면** 0건이다. ⚠️ 그 0은 **대조군과 함께** 읽어라: 같은 출력에
 *   `WEB`·`APP`·`ADMIN`이 보여야 축이 옳다(축이 틀리면 전부 0이 나와 같은 모양이다).
 *
 * 짝 spec(`__tests__/index.defaultScope.test.tsx`)이 「요청에 WEB·APP이 둘 다 실린다」를 잰다.
 */
export const DEFAULT_PUBLIC_PLATFORM_SCOPE: readonly ReleaseNotePlatform[] = [
  'WEB',
  'APP',
  'MOBILE',
  'ALL',
];

/**
 * 칩으로 고를 수 있는 단일 surface — **고객이 쓰는 surface 둘**뿐이다(2026-10-15 사용자 결정).
 *
 * 빠진 값과 사유 — **셋이 서로 다른 이유다**(한 문장으로 뭉치면 다음 사람이 잘못 되돌린다):
 * - `ADMIN`: 데이터는 **있다**(노트 318건). 그런데 고객이 접근할 수 없는 운영자 기능이라
 *   **본인에게 해당되지 않는다** — 축이 「0건」이 아니라 **「누구의 목록인가」**다(위 상수 JSDoc의
 *   2026-10-15 개정 절). ADMIN 노트를 보는 자리는 admin 화면이다.
 * - `SERVER`·`MOBILE`: 재분류로 **데이터가 0건**이라(§A ③) 칩을 두면 언제나 빈 목록을 보여준다.
 * - `ALL`은 태그이지 surface가 아니고, `INTERNAL`은 공개 피드가 **구조적으로** 0건이다(그 위에
 *   「내부용」 범주를 고객에게 보일 이유도 없다).
 *
 * ⚠️ 라벨·색 매핑은 **일곱 값 전부**를 유지한다 — 칩에서 빠진 것과 「그 값이 응답에 못 온다」는
 * 다른 문장이다(옛 행·과도기 창). `ADMIN`은 특히 그렇다: 칩이 없어도 **공개 API가 돌려주는 값**이라
 * 그룹 라벨이 그릴 수 있다. 그래서 라벨을 지우는 것은 **결함**이다.
 */
const SINGLE_PLATFORM_CHIPS: readonly ReleaseNotePlatform[] = ['WEB', 'APP'];

const isDefaultScope = (scope: readonly ReleaseNotePlatform[]) =>
  scope.length === DEFAULT_PUBLIC_PLATFORM_SCOPE.length &&
  DEFAULT_PUBLIC_PLATFORM_SCOPE.every((p) => scope.includes(p));

export default function ReleaseNotesPage() {
  const { isAuthenticated } = useAuthStore();
  const [groups, setGroups] = useState<PublicReleaseNoteDateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // 요청 surface 집합 — 기본은 「웹·앱」, 칩은 그것을 단일 surface로 좁히거나(ADMIN으로) 옮긴다.
  const [scope, setScope] = useState<readonly ReleaseNotePlatform[]>(DEFAULT_PUBLIC_PLATFORM_SCOPE);

  /**
   * 요청 세대 — **느린 앞 응답이 뒤에 도착해 최신 상태를 덮는 것**을 막는다.
   *
   * ## 왜 `PopupHost`의 `alive` 한 줄이 아닌가 — 요청원이 **둘**이다
   * `components/PopupHost.tsx`는 effect 하나만 요청하므로 cleanup에서 닫는 `alive` 플래그로 충분하다.
   * 여기는 요청하는 자리가 둘이고 **성질이 다르다**:
   * - **effect**(칩 전환) — cleanup이 있으니 `alive`로도 닫을 수 있다.
   * - **「더보기」 핸들러** — effect가 아니라 **cleanup이 없다.** 그래서 `alive`를 쓸 자리가 없다.
   *
   * 세대 카운터 하나가 둘을 함께 막으므로 한 불변식을 한 자리에서 집행한다. 기제: 요청을 시작할 때
   * 그때의 세대를 **값으로 집어 두고**, 응답을 커밋하기 전에 그것이 여전히 최신인지 본다.
   * cleanup도 세대를 올리므로 **언마운트**와 **칩 재전환**이 모두 「낡은 응답」을 만든다.
   *
   * ## 두 축이 «다르게» 깨진다 — 그래서 짝 spec의 케이스도 둘이다
   * - **칩 전환**: 앞 응답이 `setGroups`로 **덮는다**(최신 칩의 목록이 사라진다).
   * - **더보기**: 앞 scope로 띄운 요청이 `setGroups((prev) => [...prev, …])`로 **덧붙는다** —
   *   한 목록에 **두 scope의 데이터가 섞인다.** 이쪽이 더 나쁘다(덮는 것은 한 번 새로고침하면
   *   맞지만 섞인 목록은 그 자리에서 거짓이다). 그리고 `nextCursor`도 앞 scope의 것으로 밀려
   *   그 뒤 「더보기」가 엉뚱한 페이지를 청한다.
   *
   * ⚠️ `setLoadingMore(false)`는 **세대를 보지 않는다**(아래) — 보게 하면 칩 전환으로 낡은
   * 더보기 요청이 그 값을 영구히 `true`로 남겨 버튼이 「불러오는 중...」으로 **잠긴다**.
   * 반대로 `setLoading(false)`는 **보아야 한다**: 낡은 응답이 스피너를 걷으면 최신 요청이 아직
   * 도는 동안 **옛 목록이 「다 불러왔다」는 모습으로** 보인다.
   */
  const requestGenerationRef = useRef(0);

  // 초기 로드 & 필터 변경 시
  useEffect(() => {
    const generation = ++requestGenerationRef.current;
    const isStale = () => generation !== requestGenerationRef.current;

    const fetchNotes = async () => {
      try {
        setLoading(true);
        setError(null);
        setNextCursor(null);
        const response = await releaseNotesApi.getPublic({
          platforms: [...scope],
          days: 7,
        });
        if (isStale()) return;
        if (response.data) {
          setGroups(response.data.groups);
          setNextCursor(response.data.nextCursor);
        }
      } catch (err) {
        if (isStale()) return;
        setError('업데이트 내역을 불러오는데 실패했습니다.');
        console.error(err);
      } finally {
        // 낡은 응답은 스피너를 걷지 않는다 — 걷으면 최신 요청이 아직 도는 중에 옛 목록이 완성본처럼 보인다
        if (!isStale()) setLoading(false);
      }
    };

    void fetchNotes();

    // 언마운트·칩 재전환이 모두 세대를 올려 진행 중 응답을 「낡은 것」으로 만든다.
    //
    // ⚠️ 규칙이 「cleanup이 돌 때쯤 ref 값이 바뀠을 것」이라 경고하는데, 여기서는 **바뀌는 것이
    //    목적**이다. 그 경고가 겨누는 것은 「React가 렌더한 **노드**를 가리키는 ref」를 cleanup에서
    //    읽는 경우이고(그때는 옛 노드를 잡는다), 이 ref는 노드가 아니라 **공유 카운터**라 최신 값을
    //    읽는 것이 맞다. 값을 지역 변수로 복사하면 이 카운터는 거꾸로 **동작하지 않는다.**
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      requestGenerationRef.current++;
    };
  }, [scope]);

  // 더보기
  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    // 세대를 **지금** 집어 둔다 — 응답이 오기 전에 칩이 바뀌면 이 페이지는 다른 scope의 것이 된다
    const generation = requestGenerationRef.current;
    try {
      setLoadingMore(true);
      const response = await releaseNotesApi.getPublic({
        platforms: [...scope],
        days: 7,
        beforeDate: nextCursor,
      });
      if (generation !== requestGenerationRef.current) return;
      if (response.data) {
        setGroups((prev) => [...prev, ...response.data!.groups]);
        setNextCursor(response.data.nextCursor);
      }
    } catch (err) {
      console.error(err);
    } finally {
      // 세대를 **보지 않는다** — 위 JSDoc의 경고: 보게 하면 버튼이 영구히 잠긴다
      setLoadingMore(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-ink-50">
      {/* 헤더 */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="text-xl font-bold text-brand-600">
            SafetyHub
          </Link>
          {isAuthenticated ? (
            <Link
              to="/"
              className="text-sm text-ink-600 hover:text-ink-900"
            >
              대시보드
            </Link>
          ) : (
            <Link
              to="/login"
              className="text-sm text-ink-600 hover:text-ink-900"
            >
              로그인
            </Link>
          )}
        </div>
      </header>

      {/* 메인 */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-ink-900 mb-2">업데이트 내역</h1>
          <p className="text-ink-600">SafetyHub의 최신 업데이트와 변경 사항을 확인하세요.</p>
        </div>

        {/* 플랫폼 필터 — 배타 단일선택이라 radiogroup으로 읽힌다(Chip이 role/aria-checked를 통과).
            첫 칩은 기본 스코프(웹·앱)다 — 「전체」라 적지 않는다: 이 화면의 기본이 ADMIN을 빼므로
            그 라벨은 거짓이 된다(DEFAULT_PUBLIC_PLATFORM_SCOPE 주석). 라벨이 유산 두 값
            (MOBILE·ALL)을 말하지 않는 것은 의도다 — 그 둘은 데이터가 0건으로 수렴하는 과도기
            완충이고, 사용자가 고르는 축이 아니다. */}
        <div className="mb-6 flex gap-2 flex-wrap" role="radiogroup" aria-label="플랫폼 필터">
          <Chip
            role="radio"
            aria-checked={isDefaultScope(scope)}
            selected={isDefaultScope(scope)}
            onClick={() => setScope(DEFAULT_PUBLIC_PLATFORM_SCOPE)}
          >
            웹·앱
          </Chip>
          {SINGLE_PLATFORM_CHIPS.map((platform) => {
            const selected = scope.length === 1 && scope[0] === platform;
            return (
              <Chip
                key={platform}
                role="radio"
                aria-checked={selected}
                selected={selected}
                onClick={() => setScope([platform])}
              >
                {/* 폴백(`platformLabel`)을 **쓰지 않는다** — 여기 들어오는 값은 서버 응답이 아니라
                    위 `SINGLE_PLATFORM_CHIPS` 상수라, 모르는 값이 **도달할 수 없다**. 쓰면 영영
                    발동하지 않는 분기가 되고 그것은 비용이 아니라 거짓 안심이다(다음 사람이
                    「여기도 막혀 있다」를 근거로 쓴다). 그룹 라벨 쪽만 폴백을 거친다. */}
                {PLATFORM_LABELS[platform]}
              </Chip>
            );
          })}
        </div>

        {/* 콘텐츠 */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto"></div>
            <p className="mt-4 text-ink-500">로딩 중...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-rose-600">{error}</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-ink-500">업데이트 내역이 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {groups.map((dateGroup) => (
              <Card key={dateGroup.date} padding="none" radius="lg" className="overflow-hidden">
                {/* 날짜 헤더 */}
                <div className="bg-ink-50 px-6 py-3 border-b">
                  <h2 className="font-semibold text-ink-900">{formatDate(dateGroup.date)}</h2>
                </div>

                {/* 카테고리별 섹션 */}
                <div className="divide-y">
                  {dateGroup.categories.map((categoryGroup) => (
                    <div key={categoryGroup.category} className="px-6 py-4">
                      {/* 카테고리 헤더 — 분류 태그(배지) 갈래라 반지름 토큰으로 옮기지 않는다.
                          `Badge`의 사각형(`shape="rounded"`)이 목업 `.type-pill`과 같은 6px을 소유하고 있어,
                          여기서 10/12px로 스냅하면 배지 이관 때 되돌려야 한다. */}
                      {/* eslint-disable-next-line local/no-untokenized-radius */}
                      <div className={`inline-block px-3 py-1 rounded-md text-sm font-semibold mb-4 border ${CATEGORY_COLORS[categoryGroup.category]}`}>
                        {CATEGORY_LABELS[categoryGroup.category]}
                      </div>

                      {/* 플랫폼별 그룹 */}
                      <div className="space-y-4">
                        {categoryGroup.platforms.map((platformGroup) => (
                          <div key={platformGroup.platforms.join('+')} className="ml-2">
                            {/* surface 집합 레이블 + 버전 — 「웹 · 모바일」처럼 전부 나열한다(2단계부터 둘 이상).
                                색은 첫 surface(서버 PLATFORM_ORDER 순)의 것 — 구분은 라벨 텍스트가 한다. */}
                            <div className={`font-medium text-sm mb-2 ${platformColor(platformGroup.platforms[0])}`}>
                              {platformGroup.platforms.map((p) => platformLabel(p)).join(' · ')}
                              {platformGroup.version && (
                                <span className="ml-2 text-xs font-normal text-ink-400">
                                  v{platformGroup.version}
                                </span>
                              )}
                            </div>

                            {/* 항목 목록 — 제목은 「무엇이」, 본문(`content`)은 「어떻게 달라졌나」다.
                                본문은 **접지 않고 항상 보인다**(2026-10-16 사용자 결정 — prod 실측
                                평균 56자(15~159)라 접기가 거추장스럽다). 제목이 주, 본문이 종이다. */}
                            <ul className="space-y-2.5 ml-4">
                              {platformGroup.items.map((item) => {
                                // 본문 없는 노트가 다수다(prod 실측 1509건 중 `content` 보유 209건) —
                                // 빈 문자열·공백만인 값도 **요소를 만들지 않는다**(빈 여백이 남으면
                                // 「본문이 있는데 안 보인다」와 모양이 같아진다).
                                const content = item.content?.trim();
                                return (
                                  <li key={item.id} className="flex items-start gap-2">
                                    <span className="text-ink-400 mt-1.5">·</span>
                                    <div className="min-w-0">
                                      <p className="text-ink-800">{item.title}</p>
                                      {content ? (
                                        // 서버에서 온 문자열이라 **텍스트로만** 그린다
                                        // (`dangerouslySetInnerHTML` 금지). 이 화면은 무인증 공개 페이지다.
                                        //
                                        // ⚠️ `whitespace-pre-line`은 **오늘 효과가 0이다** — prod 실측
                                        // (2026-10-16) 본문 209건 중 줄바꿈을 가진 것이 **0건**이고
                                        // `**강조**` 표기도 **0건**이다(그래서 `splitEmphasis`를 쓰지
                                        // 않는다). 이 선언은 레포 파일 층
                                        // (`release-notes/notes/*.json`)에서 여러 줄 본문을 쓰게 될 때를
                                        // 위한 것이고, **지금 무언가를 하고 있다고 읽지 말 것**이다.
                                        //
                                        // ⚠️ **2026-10-26에 그 함수의 자리와 「안 쓰는 이유」가 둘 다
                                        // 바뀌었다.** 자리는 이 앱의 `lib`에서 `@safetyhub/ui`
                                        // (`packages/ui/src/popup/popupEmphasis.ts`)로 옮겼고, 종전 사유
                                        // 「아무것도 안 하면서 그 모듈을 `vi.mock`하는 spec들을
                                        // 끌어들인다」는 **뒤집혔다** — 공용 패키지로 옮겨 이 앱에서
                                        // `vi.mock`될 모듈이 아니게 됐으므로 그 비용이 **없다**. 남은
                                        // 사유는 위 「실측 0건」 하나뿐이다 ⇒ 그 실측이 바뀌면(노트
                                        // 본문이 강조를 쓰기 시작하면) **채택하는 것이 맞고**, 그때는
                                        // `POPUP_EMPHASIS_CLASS`까지 함께 가져와야 색이 팝업과 같아진다.
                                        <p className="mt-0.5 text-sm text-ink-500 whitespace-pre-line">
                                          {content}
                                        </p>
                                      ) : null}
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}

            {/* 더보기 버튼 */}
            {nextCursor && (
              <div className="text-center pt-2 pb-4">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-6 py-2.5 bg-white border border-ink-300 rounded-control text-sm font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50 transition-colors"
                >
                  {loadingMore ? '불러오는 중...' : '이전 업데이트 내역 더보기'}
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* 푸터 */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-sm text-ink-500">
          &copy; {new Date().getFullYear()} SafetyHub. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
